import { Service, signal } from '@angular/core';

import { generateUuid } from '@lvflanders/bar';

import { BaasToastMessageCloseTrigger, BaasToastMessageOptions } from './baas-toast-message-options';

/**
 * Provides bar toast messages.
 */
@Service()
export class BaasToastMessageService {
    #messages = signal<BaasToastMessageOptions[]>([]);
    messages = this.#messages.asReadonly();

    #messageRemovalBucket = {};

    /** Adds a new toast message and returns its unique id. */
    addMessage(message: BaasToastMessageOptions): string {
        const toastMessage = this.#createEnrichedToastMessage(message);
        const messages = this.#messages();
        // prevent duplicate messages
        // requirement for duplicates: same text & age < 500ms (must have text prop set)
        let foundDuplicate: any = false;
        if (message.text) {
            const now = Date.now();
            foundDuplicate = Object.keys(this.#messageRemovalBucket).find((key: string): boolean => {
                if (this.#messageRemovalBucket[key].message.text)
                    return this.#messageRemovalBucket[key].message.text === message.text && now - this.#messageRemovalBucket[key].time < 500;
                else return false;
            });
        }

        if (!foundDuplicate) {
            this.#messages.set(messages && messages.length ? [...messages, toastMessage] : [toastMessage]);

            if (!toastMessage.persistent) {
                this.#messageRemovalBucket[toastMessage.id] = {
                    timer: setTimeout(() => {
                        this.#removeMessage(toastMessage.id, 'timeout');
                    }, toastMessage.duration),
                    time: Date.now(),
                    message: message
                };
            }
        }
        return toastMessage.id;
    }

    /**
     * Removes a message by id.
     * @param id The message id to remove
     * @param _reason Internal use only - do not pass this parameter
     * @internal
     */
    removeMessage(id: string, _trigger?: BaasToastMessageCloseTrigger): void {
        this.#removeMessage(id, _trigger ?? 'manual');
    }

    removeMessages(): void {
        const messagesToRemove = this.#messages();
        messagesToRemove.forEach((message) => {
            message.onClose?.({ trigger: 'manual', id: message.id });
        });
        this.#messages.set([]);
        Object.keys(this.#messageRemovalBucket).forEach((id) => {
            this.#deleteFromBucket(id);
        });
    }

    #removeMessage(id: string, trigger: BaasToastMessageCloseTrigger): void {
        const message = this.#messages().find((m) => m.id === id);
        if (message) {
            message.onClose?.({ trigger, id });
        }
        this.#messages.update((messages) => messages.filter((m) => m.id !== id));
        if (this.#messageRemovalBucket[id]) this.#deleteFromBucket(id);
    }

    #deleteFromBucket(id): void {
        clearTimeout(this.#messageRemovalBucket[id].timer);
        delete this.#messageRemovalBucket[id];
    }

    #createEnrichedToastMessage(message: BaasToastMessageOptions) {
        const newMessage = {} as BaasToastMessageOptions;
        newMessage.id = message.id ? message.id : generateUuid();
        newMessage.onClose = message.onClose;
        newMessage.text = message.text;
        newMessage.icon = message.icon;
        newMessage.type = message.type ? message.type : 'warning';
        newMessage.persistent = message.persistent === undefined ? false : message.persistent;
        newMessage.component = message.component;
        newMessage.componentParameters = message.componentParameters;
        newMessage._element = message._element;
        if (!message.duration && message.text) {
            const textLength = Math.floor(message.text.length / 120);
            newMessage.duration = 5000 + textLength * 1000;
        } else {
            newMessage.duration = !message.duration || (message.duration && message.duration < 5000) ? 5000 : message.duration;
        }
        return newMessage;
    }
}
